/**
 * useStorefront — Bundle 7.B (house/sub two-tier model)
 *
 * Reads the house affiliate from:
 *   1. aff_house cookie (set by AffAttributionMiddleware, first-touch)
 *   2. URL ?aff= param (before first server response sets the cookie)
 *   3. lvs_ref cookie (legacy ?ref= system)
 *
 * Never reads aff_sub for display — customers only see house branding.
 * Returns null while loading or if no storefront exists.
 */
import { useEffect, useState } from 'react';

export interface StorefrontConfig {
  slug: string;
  display_name: string;
  tagline: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  powered_by_visible: boolean;
}

function getRefToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  const urlRef = params.get('ref');
  if (urlRef) return urlRef;
  const match = document.cookie.match(/(?:^|;\s*)lvs_ref=([^;]+)/);
  return match ? match[1] : null;
}

/**
 * Bundle 7.B — reads aff_house cookie (set server-side, first-touch) or
 * falls back to ?aff= URL param (before the server response has set the cookie).
 * Always returns a house-level code; the server normalises subs to their house.
 */
function getAffHouseCode(): string | null {
  const cookieMatch = document.cookie.match(/(?:^|;\s*)aff_house=([^;]+)/);
  if (cookieMatch) return decodeURIComponent(cookieMatch[1]);
  const params = new URLSearchParams(window.location.search);
  return params.get('aff');
}

export function useStorefront(): StorefrontConfig | null {
  const [storefront, setStorefront] = useState<StorefrontConfig | null>(null);

  useEffect(() => {
    const affHouseCode = getAffHouseCode();
    const refToken = getRefToken();

    if (!affHouseCode && !refToken) return;

    const fetchStorefront = async () => {
      // Priority: aff_house (Bundle 7.B) → lvs_ref (legacy).
      // aff_house is always house-level. Storefronts exist only for houses.
      if (affHouseCode) {
        try {
          const res = await fetch(
            `/api/affiliate/storefront-by-aff/${encodeURIComponent(affHouseCode)}/`
          );
          if (res.ok) {
            const data: StorefrontConfig = await res.json();
            setStorefront(data);
            return;
          }
        } catch (_) { /* fall through to lvs_ref */ }
      }

      if (refToken) {
        try {
          const res = await fetch(
            `/api/affiliate/storefront-by-ref/${encodeURIComponent(refToken)}/`
          );
          if (res.ok) {
            const data: StorefrontConfig = await res.json();
            setStorefront(data);
          }
        } catch (_) { /* silently fail — fall back to default branding */ }
      }
    };

    fetchStorefront();
  }, []);

  return storefront;
}
